import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Stack, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
} from '@mui/icons-material';
import wineListFormatService from '../../services/wineListFormatService';
import type { WineListFormat } from '../../types/venue';

interface Props {
  venueId: string;
  wineListId: string;
}

interface FormState {
  name: string;
  volumeMl: string;
  position: string;
}

const emptyForm: FormState = { name: '', volumeMl: '', position: '' };

/**
 * Admin format catalog for a wine list.
 * Formats are catalogue entries (name + volume); per-item pricing is set on
 * the item form. No availability / inventory implications.
 */
const WineListFormatsPanel: React.FC<Props> = ({ venueId, wineListId }) => {
  const theme = useTheme();
  const [formats, setFormats] = useState<WineListFormat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WineListFormat | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError('');
    wineListFormatService.getFormats(venueId, wineListId)
      .then(setFormats)
      .catch(() => setError('Could not load formats. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [venueId, wineListId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, position: String(formats.length) });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (f: WineListFormat) => {
    setEditing(f);
    setForm({
      name: f.name,
      volumeMl: String(f.volumeMl),
      position: String(f.position ?? 0),
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    const volumeNum = Number(form.volumeMl);
    if (!isFinite(volumeNum) || volumeNum < 1) {
      setFormError('Volume must be a positive number (ml).');
      return;
    }
    const positionNum = form.position === '' ? 0 : Number(form.position);
    if (!isFinite(positionNum)) { setFormError('Position must be a number.'); return; }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name: form.name.trim(),
        volumeMl: volumeNum,
        position: positionNum,
      };
      if (editing) {
        await wineListFormatService.updateFormat(venueId, wineListId, editing._id, payload);
      } else {
        await wineListFormatService.createFormat(venueId, wineListId, payload);
      }
      setDialogOpen(false);
      refresh();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'FORMAT_NAME_DUPLICATE') {
        setFormError('A format with this name already exists on this list.');
      } else {
        setFormError(err?.response?.data?.message ?? 'Could not save the format. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f: WineListFormat) => {
    if (!window.confirm(`Delete format "${f.name}"? Existing per-item prices for this format will stop showing on the customer view but are not erased.`)) return;
    try {
      await wineListFormatService.deleteFormat(venueId, wineListId, f._id);
      refresh();
    } catch {
      setError('Could not delete the format. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Formats</Typography>
          <Typography variant="caption" color="text.secondary">
            Pour formats sold on this list (e.g. Glass 150 ml, Coravin 60 ml). Bottle is implicit. Per-wine prices are set on each wine entry.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          New format
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 1, mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : formats.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No formats yet. Wines will only show their bottle price on the customer view.
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1, textTransform: 'none' }}>
            Create first format
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {formats.map(f => (
            <Paper
              key={f._id}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 1, backgroundColor: alpha(theme.palette.primary.main, 0.08),
                fontSize: '0.78rem', fontFamily: 'monospace', color: theme.palette.text.secondary,
              }}>
                {f.position}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={600} noWrap>{f.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {f.volumeMl} ml
                </Typography>
              </Box>
              <Tooltip title="Edit format">
                <IconButton size="small" onClick={() => openEdit(f)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete format">
                <IconButton size="small" color="error" onClick={() => handleDelete(f)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit format' : 'New format'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>{formError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              autoFocus
              fullWidth
              required
              placeholder="e.g. Glass, Half Glass, Coravin"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Volume (ml)"
                value={form.volumeMl}
                onChange={e => setForm(f => ({ ...f, volumeMl: e.target.value }))}
                type="number"
                required
                sx={{ flex: 1 }}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
              />
              <TextField
                label="Position"
                value={form.position}
                onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                type="number"
                sx={{ width: 120 }}
                helperText="Lower = earlier"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, minWidth: 100 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WineListFormatsPanel;
